<template>
  <div class="base-layout">
    <header class="shadow-2">
      <Menubar class="main-menubar">
        <template #start>
          <div class="logo-section">
            <router-link to="/" v-slot="{ href }" custom>
              <a :href="href" class="logo-link">
                <img :src="Logo" alt="Feedwiz Home" class="logo-image">
                <div class="logo-text-wrapper">
                  <span class="logo-title">Feedwiz</span>
                  <span class="logo-tagline">Parse, Extract & Transform data with simplicity</span>
                </div>
              </a>
            </router-link>
          </div>
        </template>
        <template #end>
          <div class="nav-actions">
            <template v-if="isAuthenticated">
              <div class="user-menu-trigger" @click="toggleUserMenu">
                <Avatar :label="userInitials" shape="circle" size="small" class="user-avatar"/>
                <span class="user-name">{{ userName }}</span>
                <i class="pi pi-angle-down user-dropdown-icon"></i>
              </div>
              <Menu ref="userMenu" :model="userMenuItems" :popup="true"/>
            </template>
            <template v-else>
              <router-link to="/login" v-slot="{ href }" custom>
                <a :href="href" class="no-underline">
                  <Button
                      class="btn-login"
                      icon="pi pi-sign-in"
                      label="Login"
                      aria-label="Login"
                  />
                </a>
              </router-link>
              <router-link to="/signup" v-slot="{ href }" custom>
                <a :href="href" class="no-underline">
                  <Button
                      class="btn-signup"
                      icon="pi pi-user-plus"
                      label="Sign Up"
                      aria-label="Sign Up"
                  />
                </a>
              </router-link>
            </template>
          </div>
        </template>
      </Menubar>
    </header>
    <main>
      <div class="main-content">
        <slot></slot>
      </div>
    </main>
  </div>
</template>

<script setup>
import {computed, ref, onMounted} from 'vue';
import {useStore} from 'vuex';
import {useRouter} from 'vue-router';
import Menubar from 'primevue/menubar';
import Button from 'primevue/button';
import Avatar from 'primevue/avatar';
import Menu from 'primevue/menu';
import Logo from '@/assets/feedwiz-logo-white-new-2.png';

// eslint-disable-next-line no-undef,no-unused-vars
const props = defineProps({
  menuLinks: {
    type: Array,
    required: true
  }
});

const router = useRouter();
// const route = useRoute();

const store = useStore();
const isAuthenticated = computed(() => store.getters.isAuthenticated);
const user = computed(() => store.getters.user);

// User display properties
const userName = computed(() => {
  if (!user.value) return '';
  return user.value.name || user.value.email || '';
});

const userInitials = computed(() => {
  if (!userName.value) return '';

  // Get initials from name or first letter of email
  if (user.value?.name) {
    return user.value.name
        .split(' ')
        .map(part => part.charAt(0).toUpperCase())
        .join('')
        .substring(0, 2);
  } else if (user.value?.email) {
    return user.value.email.charAt(0).toUpperCase();
  }

  return '';
});

// Logout handler
const handleLogout = async () => {
  await store.dispatch('logout');
  router.push('/login');
};

// Fetch user data on component mount if authenticated
onMounted(() => {
  if (isAuthenticated.value) {
    store.dispatch('fetchCurrentUser');
  }
});

// User menu
const userMenu = ref();
const userMenuItems = ref([
  {
    label: 'User Settings',
    icon: 'pi pi-cog',
    command: () => router.push('/user-settings')
  },
  {
    label: 'Logout',
    icon: 'pi pi-sign-out',
    command: () => handleLogout()
  },

]);

// Toggle user menu
const toggleUserMenu = (event) => {
  userMenu.value.toggle(event);
};

</script>

<style scoped>
/* Mobile-First Approach - Base styles for iPhone SE (375x667) */
.base-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Main Menubar */
.main-menubar {
  display: flex;
  justify-content: space-between;
  border: none;
  background: linear-gradient(135deg, #4a89dc 0%, #5ca0f2 50%, #67b7f7 100%);
  transition: all 0.3s ease;
  box-shadow: 0 3px 10px rgba(0,0,0,0.2);
  padding: 0.5rem;
}

/* Logo Section - Mobile First */
.logo-section {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 0.5rem;
}

.logo-image {
  height: 3rem; /* Mobile: smaller logo */
  width: auto;
}

.logo-text-wrapper {
  display: flex;
  flex-direction: column;
}

.logo-title {
  color: white;
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem; /* Mobile: smaller text */
  font-weight: 700;
  letter-spacing: -0.02em;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
}

.logo-tagline {
  display: none; /* Mobile: hide tagline */
  color: white;
  font-family: 'Inter', sans-serif;
  font-style: italic;
  font-size: 0.75rem;
  font-weight: 400;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
}

/* Navigation Actions - Mobile First */
.nav-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.5rem;
}

/* User Menu - Mobile First */
.user-menu-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.25rem;
  transition: background-color 0.2s;
}

.user-menu-trigger:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.user-avatar {
  background-color: var(--blue-600);
  color: white;
}

.user-name {
  display: none; /* Mobile: hide username */
  color: white;
  font-weight: 500;
  margin: 0 0.5rem;
}

.user-dropdown-icon {
  color: white;
  margin-left: 0; /* Mobile: no margin */
}

/* Buttons - Mobile First */
.btn-login,
.btn-signup {
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-login {
  background-color: var(--primary-color);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.btn-login:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.btn-signup {
  background-color: white;
  color: var(--blue-600);
  border-color: white;
}

/* Mobile: Icon-only buttons */
.btn-login :deep(.p-button-label),
.btn-signup :deep(.p-button-label) {
  display: none;
}

/* Main Content */
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0.75rem; /* Mobile: smaller padding */
}

/* ===== SMALL TABLET (576px+) ===== */
@media (min-width: 576px) {
  .logo-tagline {
    display: block; /* Small tablet: show tagline */
    font-size: 0.75rem;
  }

  .nav-actions {
    gap: 0.75rem;
  }

  /* Show button labels on small tablet */
  .btn-login :deep(.p-button-label),
  .btn-signup :deep(.p-button-label) {
    display: inline;
    margin-left: 0.5rem;
  }
}

/* ===== MEDIUM TABLET (768px+) ===== */
@media (min-width: 768px) {
  .main-menubar {
    padding: 0.75rem 1rem;
  }

  .logo-image {
    height: 4rem; /* Medium tablet: larger logo */
  }

  .logo-title {
    font-size: 1.75rem; /* Medium tablet: larger text */
  }

  .logo-tagline {
    font-size: 0.875rem;
  }

  .nav-actions {
    padding: 0 0.75rem;
  }

  .user-name {
    display: inline; /* Medium tablet: show username */
  }

  .user-dropdown-icon {
    margin-left: 0.5rem; /* Medium tablet: add margin */
  }

  .main-content {
    padding: 1rem;
  }
}

/* ===== LARGE DESKTOP (992px+) ===== */
@media (min-width: 992px) {
  .main-menubar {
    padding: 1rem 1.5rem;
  }

  .logo-link {
    gap: 0.75rem;
  }

  .logo-image {
    height: 5rem; /* Desktop: full size logo */
  }

  .logo-title {
    font-size: 2.5rem; /* Desktop: full size text */
  }

  .nav-actions {
    gap: 1rem;
    padding: 0 1rem;
  }

  .main-content {
    padding: 1.5rem;
    max-width: 1920px;
    margin: 0 auto;
    width: 100%;
  }
}
</style>
